import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Users, UserPlus, Trash2, Shield, Mail, Loader2, Pencil, X, Check, Bell
} from 'lucide-react';

function UserRow({ user, currentUserId, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState(user.role);
  const [isActive, setIsActive] = useState(user.is_active);
  const [reportEnabled, setReportEnabled] = useState(user.report_enabled);
  const [saving, setSaving] = useState(false);
  const isSelf = user.id === currentUserId;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(user.id, { name, role, is_active: isActive, report_enabled: reportEnabled });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setName(user.name);
    setRole(user.role);
    setIsActive(user.is_active);
    setReportEnabled(user.report_enabled);
    setEditing(false);
  };

  return (
    <div
      data-testid={`user-row-${user.id}`}
      className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${
        !isActive ? 'opacity-50 bg-muted/10' : 'bg-card/50 hover:bg-card'
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {editing ? (
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-7 text-sm w-40"
              placeholder="Name"
              data-testid="edit-name"
            />
          ) : (
            <span className="text-sm font-medium">{user.name || '—'}</span>
          )}
          <Badge
            variant={user.role === 'admin' ? 'default' : 'secondary'}
            className="text-[10px]"
          >
            <Shield className="w-2.5 h-2.5 mr-1" />
            {editing ? (
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="bg-transparent text-[10px] outline-none"
                data-testid="edit-role"
              >
                <option value="admin">admin</option>
                <option value="operator">operator</option>
              </select>
            ) : user.role}
          </Badge>
          {isSelf && <Badge variant="outline" className="text-[9px]">You</Badge>}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="font-mono flex items-center gap-1">
            <Mail className="w-3 h-3" /> {user.email}
          </span>
          {user.report_enabled && (
            <span className="flex items-center gap-1 text-amber-400">
              <Bell className="w-3 h-3" /> Report active
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {editing && (
          <>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-muted-foreground">Active</span>
              <Switch
                checked={isActive}
                onCheckedChange={setIsActive}
                data-testid="edit-active"
              />
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-muted-foreground">Report</span>
              <Switch
                checked={reportEnabled}
                onCheckedChange={setReportEnabled}
                data-testid="edit-report"
              />
            </div>
          </>
        )}

        {editing ? (
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" onClick={handleSave} disabled={saving} data-testid="save-user-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 text-green-400" />}
            </Button>
            <Button size="icon" variant="ghost" onClick={handleCancel} data-testid="cancel-edit-btn">
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        ) : (
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" onClick={() => setEditing(true)} data-testid="edit-user-btn">
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            {!isSelf && (
              <Button
                size="icon"
                variant="ghost"
                onClick={() => onDelete(user.id, user.email)}
                className="hover:text-red-400"
                data-testid="delete-user-btn"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);

  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('customer');

  // Get current user ID from token
  const currentUserId = (() => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch { return null; }
  })();

  const loadUsers = useCallback(async () => {
    try {
      const data = await api.getUsers();
      setUsers(data);
    } catch (err) {
      console.error('Load users error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.createUser({
        email: newEmail,
        password: newPassword,
        name: newName,
        role: newRole,
      });
      setNewEmail(''); setNewPassword(''); setNewName(''); setNewRole('operator');
      setShowCreate(false);
      loadUsers();
    } catch (err) {
      alert('Error: ' + (err.message || 'Could not create user'));
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async (userId, data) => {
    try {
      await api.updateUser(userId, data);
      loadUsers();
    } catch (err) {
      alert('Error: ' + (err.message || 'Update failed'));
    }
  };

  const handleDelete = async (userId, email) => {
    if (!window.confirm(`Delete user "${email}"?`)) return;
    try {
      await api.deleteUser(userId);
      loadUsers();
    } catch (err) {
      alert('Error: ' + (err.message || 'Delete failed'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">User Management</h1>
          <p className="text-sm text-muted-foreground mt-1">{users.length} users</p>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)} data-testid="add-user-btn">
          <UserPlus className="w-4 h-4 mr-2" />
          New User
        </Button>
      </div>

      {/* Create User Form */}
      {showCreate && (
        <Card data-testid="create-user-form">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
              <UserPlus className="w-4 h-4" /> Create New User
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="grid grid-cols-2 gap-3">
              <Input
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Email"
                type="email"
                required
                className="text-sm"
                data-testid="new-user-email"
              />
              <Input
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Password"
                type="password"
                required
                className="text-sm"
                data-testid="new-user-password"
              />
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Name (optional)"
                className="text-sm"
                data-testid="new-user-name"
              />
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="new-user-role"
              >
                <option value="customer">Customer</option>
                <option value="admin">Admin</option>
              </select>
              <div className="col-span-2 flex gap-2">
                <Button type="submit" disabled={creating} data-testid="create-user-submit">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  Create
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* User List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <Users className="w-4 h-4" /> All Users
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {users.map(user => (
            <UserRow
              key={user.id}
              user={user}
              currentUserId={currentUserId}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
            />
          ))}
          {users.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">No users found</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
